import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Pill} from 'src/design/atoms/Pill';
import {
  ActiveInactivePills,
  VerificationPills,
} from 'src/design/atoms/pillPresets';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {EntityActiveStatusPanel} from 'src/pages/Modules/Clients/shared/EntityActiveStatusPanel';
import {
  DeviceDocumentTitle,
  DeviceViewGeneralMeta,
} from 'src/pages/Modules/Inspections/shared/deviceHelpers';
import {DeviceHistorySection} from 'src/pages/Modules/Inspections/Devices/DeviceView/components/DeviceHistorySection';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useDeleteDeviceMutation,
  useGetDeviceQuery,
  useUpdateDeviceActiveStatusMutation,
} from 'src/redux/queries/device';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  useVerifyDevice,
  VerifyDeviceDialogCopy,
} from 'src/pages/Modules/Inspections/shared/useVerifyDevice';
import type {IFormDataV2} from 'src/types/FormV2';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const DeviceViewPage = () => {
  const navigate = useAppNavigate();
  const {deviceId} = useParams();
  const {setAlert} = useAlert();
  const [isDeleting, setIsDeleting] = useState(false);
  const inspectionPerms = useInspectionPermissions();
  const {verify, isVerifying} = useVerifyDevice();
  useSetDocumentTitle(DeviceDocumentTitle);

  const validDeviceId =
    deviceId !== undefined && !isNaN(+deviceId) ? +deviceId : undefined;

  const {data: viewItem, isLoading} = useGetDeviceQuery(validDeviceId!, {
    skip: !validDeviceId,
  });

  const [deleteDevice] = useDeleteDeviceMutation();
  const [updateDeviceActiveStatus, {isLoading: isTogglingActive}] =
    useUpdateDeviceActiveStatusMutation();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [confirmVerifyOpened, confirmVerifyHandlers] = useDisclosure(false);

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!deviceId) {
        setAlert('ID not found.', 'error');
        return;
      }

      setIsDeleting(true);

      await deleteDevice(+deviceId).unwrap();

      setAlert('Device was successfully deleted.', 'success');

      navigate(PathUtility.Inspections('devices'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDeleting(false);
  };

  const handleVerify = async () => {
    confirmVerifyHandlers.close();

    if (!deviceId) {
      setAlert('ID not found.', 'error');
      return;
    }

    await verify(+deviceId, viewItem?.name);
  };

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (!viewItem) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <Alert color="red" title="Not Found">
          Device not found.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.Inspections('devices')}
          fallbackLabel="Back to Devices"
          breadcrumbs={[
            {label: 'Devices', to: PathUtility.Inspections('devices')},
            {label: viewItem.name ?? 'Device'},
          ]}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkInspections.icon}
            subtitle={viewItem.serialNumber}>
            {viewItem.name ?? 'Device'}
          </PageHeader.Title>

          <PageHeader.Badges>
            <Pill {...ActiveInactivePills(viewItem.active)} />
            <Pill {...VerificationPills(viewItem.isVerified)} />
          </PageHeader.Badges>

          <PageHeader.Actions>
            {inspectionPerms.update && !viewItem.isVerified && (
              <PageHeader.Action
                variant="primary"
                onClick={() => confirmVerifyHandlers.open()}
                loading={isVerifying}>
                Verify
              </PageHeader.Action>
            )}
            {inspectionPerms.update && (
              <PageHeader.Action
                variant="edit"
                onClick={() => navigate(PathUtility.DevicesEdit(deviceId!))}>
                Edit
              </PageHeader.Action>
            )}
            {inspectionPerms.delete && (
              <PageHeader.Action
                variant="delete"
                onClick={() => confirmDeleteHandlers.open()}
                loading={isDeleting}>
                Delete
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <SectionCard title="Site & Equipment Type">
          <Group p="md">
            <Anchor
              component={Link}
              to={PathUtility.SitesView(viewItem.siteId)}>
              {viewItem.siteName ?? 'View Site'}
            </Anchor>
            <Text size="sm" c="dimmed">
              •
            </Text>
            <Anchor
              component={Link}
              to={PathUtility.EquipmentTypesView(viewItem.equipmentTypeId)}>
              {viewItem.equipmentTypeName ?? 'View Equipment Type'}
            </Anchor>
          </Group>
        </SectionCard>

        <FormViewComponentV2
          formData={viewItem as IFormDataV2}
          formMeta={DeviceViewGeneralMeta}
        />

        <DeviceHistorySection deviceId={viewItem.id} />

        {inspectionPerms.update && (
          <EntityActiveStatusPanel
            entityLabel="Device"
            isActive={!!viewItem.active}
            loading={isTogglingActive}
            onToggle={() =>
              updateDeviceActiveStatus({
                id: viewItem.id,
                active: !viewItem.active,
              }).unwrap()
            }
          />
        )}
      </Stack>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this device?"
        body="This action cannot be undone."
      />

      <ConfirmDialog
        opened={confirmVerifyOpened}
        onClose={() => confirmVerifyHandlers.close()}
        onConfirm={handleVerify}
        confirmationButtonColor="green"
        confirmationButtonDisabled={isVerifying}
        title={VerifyDeviceDialogCopy.title}
        body={VerifyDeviceDialogCopy.body}
      />
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.DevicesView(':deviceId'),
  element: <DeviceViewPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
